// Spanish → English translation dictionary for backend HTTPException messages.
// Used by formatApiErrorDetail() in ./api.js to display English error messages
// in the US site when the user has selected "en" as their language.
//
// The keys are the exact Spanish strings raised as HTTPException detail. Whenever
// a new backend error is added, add its translation here.
//
// For messages with dynamic interpolation (Python f-strings like
// "Stock insuficiente para {sku} (disponibles: {n})"), we use regex-based
// patterns below (see DYNAMIC_ERROR_PATTERNS). The runtime looks up the
// exact dictionary first, then falls back to the pattern list.

export const ERROR_MESSAGE_MAP = {
    // Auth
    "No autenticado": "Not authenticated",
    "No autorizado": "Not authorized",
    "Usuario no encontrado": "User not found",
    "Token expirado": "Session expired",
    "Token inválido": "Invalid session",
    "Se requieren permisos de administrador": "Administrator permissions required",
    "Solo admin": "Admin only",
    "Se requiere admin para ver todos": "Admin required to view all",
    "Acceso solo para vendedores verificados": "Access limited to verified sellers",
    "Tu cuenta de vendedor requiere verificación de identidad": "Your seller account requires identity verification",
    "Este correo ya está registrado": "This email is already registered",
    "Debes aceptar los Términos y Condiciones para crear tu cuenta.": "You must accept the Terms & Conditions to create your account.",
    "Credenciales inválidas": "Invalid credentials",
    "Contraseña incorrecta": "Incorrect password",
    "Contraseña incorrecta.": "Incorrect password.",
    "La contraseña actual es incorrecta.": "The current password is incorrect.",
    "La nueva contraseña debe tener al menos 6 caracteres.": "The new password must be at least 6 characters.",
    "Enlace inválido o ya utilizado.": "Invalid or already used link.",
    "Enlace inválido.": "Invalid link.",
    "El enlace ha expirado. Solicita uno nuevo.": "The link has expired. Request a new one.",
    "session_id requerido": "session_id required",
    "Sesión inválida: {e}": "Invalid session",
    "ID inválido": "Invalid ID",
    "ID de usuario inválido": "Invalid user ID",
    "Rol inválido": "Invalid role",
    "No puedes eliminarte a ti mismo": "You cannot delete yourself",

    // Products & shop
    "Producto no encontrado": "Product not found",
    "Producto no encontrado.": "Product not found.",
    "Este producto no es tuyo": "This product is not yours",
    "SKU {sku} ya existe": "SKU already exists",
    "Agrega al menos una foto del producto.": "Add at least one product photo.",
    "Nada que actualizar": "Nothing to update",
    "Sin campos para actualizar": "No fields to update",
    "Nombre y apellido son requeridos": "First and last name are required",
    "Falta el campo ": "Missing field ",
    "Faltan campos: {": "Missing fields: {",

    // Cart / checkout
    "Cantidad inválida.": "Invalid quantity.",
    "Método de pago no válido. Solo transferencia o PayPal.": "Invalid payment method. Only transfer or PayPal.",
    "Solo se aceptan Tarjeta o PayPal para envíos a Estados Unidos": "Only Card or PayPal are accepted for shipments to the United States",
    "Este método de pago está temporalmente desactivado": "This payment method is temporarily disabled",
    "En Estados Unidos solo está disponible envío (sin retiro en tienda)": "Only shipping is available in the United States (no in-store pickup)",
    "Este pedido no requiere envío": "This order does not require shipping",
    "Confirma primero el pago del pedido": "Please confirm the order payment first",
    "Este pedido no tiene etiqueta generada todavía": "This order does not have a label generated yet",
    "Este pedido no es de Estados Unidos": "This order is not from the United States",
    "Pedido no encontrado": "Order not found",
    "Pedido sin PayPal order id": "Order missing PayPal order id",
    "Este pedido no contiene productos tuyos": "This order does not contain your products",
    "Solo pedidos SV con envío usan C807": "Only SV orders with shipping use C807",
    "Solo pedidos SV usan C807. Para USA usa ShipStation.": "Only SV orders use C807. For USA use ShipStation.",
    "Solo pedidos de USA": "USA orders only",
    "Solo puedes devolver pedidos pagados": "You can only return paid orders",
    "No puedes abrir devolución para este pedido": "You cannot open a return for this order",
    "El apartado en tienda solo está disponible para usuarios verificados. Verifica tu identidad desde tu perfil.": "In-store hold is only available to verified users. Verify your identity from your profile.",
    "El Pago Contra Entrega (CCE) solo está disponible para usuarios verificados. Verifica tu identidad desde tu perfil.": "Cash on Delivery (COD) is only available to verified users. Verify your identity from your profile.",
    "Pago contra entrega (CCE) solo aplica para envíos C807 en El Salvador con servicio CCE": "Cash on Delivery (COD) only applies to C807 shipments in El Salvador with COD service",
    "El sitio está en mantenimiento. Vuelve a intentarlo en unos minutos.": "The site is under maintenance. Please try again in a few minutes.",
    "Las compras estarán disponibles en el lanzamiento. ¡Pronto podrás comprar!": "Purchases will be available at launch. You'll be able to shop soon!",

    // USA shipping
    "Estado (2 letras) y código ZIP son requeridos para envíos a USA": "State (2 letters) and ZIP code are required for USA shipments",
    "Estado de 2 letras y código postal son requeridos.": "2-letter state and ZIP code are required.",
    "Estado debe ser 2 letras": "State must be 2 letters",
    "Estado debe ser 2 letras (ej. CA, TX)": "State must be 2 letters (e.g. CA, TX)",
    "Estado inválido": "Invalid state",
    "La ciudad es requerida para envíos a USA": "City is required for USA shipments",
    "Servicio de envío para Estados Unidos aún no está configurado por el administrador.": "Shipping service for the United States has not been configured by the admin yet.",

    // Discount codes / coupons
    "El código es requerido": "The code is required",
    "Tipo inválido": "Invalid type",
    "Tipo inválido (fixed o percent)": "Invalid type (fixed or percent)",
    "Tipo de descuento inválido (percent o fixed).": "Invalid discount type (percent or fixed).",
    "Scope inválido (all o categories).": "Invalid scope (all or categories).",
    "El monto debe ser mayor a 0": "The amount must be greater than 0",
    "El monto no puede ser 0": "The amount cannot be 0",
    "Valor debe ser mayor que 0.": "Value must be greater than 0.",
    "El porcentaje no puede ser mayor a 90%": "The percentage cannot be greater than 90%",
    "El descuento porcentual no puede ser mayor a 90%.": "The percentage discount cannot be greater than 90%.",
    "Código no encontrado": "Code not found",
    "Ingresa un código": "Enter a code",
    "Código inválido o desactivado": "Invalid or deactivated code",
    "Código de descuento inválido o desactivado": "Invalid or deactivated discount code",
    "Cupón no encontrado": "Coupon not found",
    "Este código ha alcanzado el límite de usos": "This code has reached its usage limit",
    "Este cupón no aplica a los productos en tu carrito": "This coupon does not apply to the products in your cart",
    "Este cupón solo aplica a los productos de un vendedor específico": "This coupon only applies to products from a specific seller",

    // Reviews
    "La calificación debe ser entre 1 y 5 estrellas.": "The rating must be between 1 and 5 stars.",
    "La calificación debe ser entre 1 y 5.": "The rating must be between 1 and 5.",
    "La reseña debe tener al menos 10 caracteres.": "The review must be at least 10 characters.",
    "La reseña es demasiado larga (máx 2000 caracteres).": "The review is too long (max 2000 characters).",
    "Ya escribiste una reseña para este producto. Puedes editarla.": "You already wrote a review for this product. You can edit it.",
    "Ya enviaste una reseña para este país. Edítala desde tu perfil.": "You already submitted a review for this country. Edit it from your profile.",
    "Reseña no encontrada": "Review not found",

    // Tickets / support
    "Ticket no encontrado": "Ticket not found",
    "Comparte un mensaje para poder ayudarte.": "Share a message so we can help you.",
    "El mensaje es demasiado largo (máx 4000 caracteres).": "The message is too long (max 4000 characters).",

    // Verification & seller onboarding
    "La foto del documento debe ser una imagen (JPG/PNG).": "The document photo must be an image (JPG/PNG).",
    "La selfie debe ser una imagen (JPG/PNG).": "The selfie must be an image (JPG/PNG).",
    "Debes enviar AMBAS fotos: una selfie y una foto de tu documento de identidad.": "You must send BOTH photos: a selfie and a photo of your ID.",
    "El usuario debe estar verificado (check azul) antes de ser vendedor": "The user must be verified (blue check) before becoming a seller",
    "Tu cuenta debe estar verificada (check azul) para solicitar productos.": "Your account must be verified (blue check) to request products.",
    "Solo cuentas verificadas (check azul) pueden apartar productos en camino.": "Only verified accounts (blue check) can reserve incoming products.",

    // Product requests / suggestions
    "Solicitud no encontrada": "Request not found",
    "Sugerencia no encontrada": "Suggestion not found",
    "Vendedor no encontrado": "Seller not found",
    "Producto en camino no encontrado": "Incoming product not found",
    "Este producto no acepta apartados anticipados.": "This product does not accept advance holds.",
    "Este producto ya no acepta reservas.": "This product no longer accepts reservations.",
    "Máximo 5 prendas por solicitud (puedes negociar más durante el proceso).": "Maximum 5 items per request (you can negotiate more during the process).",
    "Agrega al menos una prenda.": "Add at least one item.",
    "Selecciona al menos una colección.": "Select at least one collection.",
    "Esta solicitud ya no puede cancelarse.": "This request can no longer be canceled.",
    "La razón debe tener al menos 10 caracteres": "The reason must be at least 10 characters",
    "El motivo del rechazo es requerido": "The rejection reason is required",
    "Devolución no encontrada": "Return not found",
    "Cargo no encontrado": "Charge not found",
    "Transacción no encontrada": "Transaction not found",
    "El saldo del usuario no puede quedar negativo": "The user's balance cannot go negative",
    "No tienes saldo disponible para aplicar": "You have no balance available to apply",

    // Campaign
    "La campaña de sugerencias no está activa en este momento.": "The suggestions campaign is not active at this time.",
    "Tu sugerencia debe tener al menos 10 caracteres.": "Your suggestion must be at least 10 characters.",
    "Tu sugerencia es demasiado larga (máx 2000 caracteres).": "Your suggestion is too long (max 2000 characters).",
    "Ya participaste en la campaña de {month}. ¡Gracias! Espera el próximo ciclo.": "You already participated in this month's campaign. Thanks! Wait for the next cycle.",

    // Country
    "País inválido. Usa SV o US.": "Invalid country. Use SV or US.",

    // Payments / gateways
    "Stripe no está configurado": "Stripe is not configured",
    "Stripe no está configurado. Configura las llaves en Admin → Ajustes → Credenciales Stripe.": "Stripe is not configured. Set the keys in Admin → Settings → Stripe Credentials.",
    "PayPal no configurado. El administrador debe agregar las credenciales.": "PayPal is not configured. The admin must add the credentials.",
    "Modo inválido — usa ": "Invalid mode — use ",
    "Modo inválido. Debe ser: live, prelaunch o maintenance": "Invalid mode. Must be: live, prelaunch or maintenance",

    // C807 / ShipStation infra
    "C807 solo opera dentro de El Salvador": "C807 only operates inside El Salvador",
    "Módulo C807 no disponible": "C807 module not available",
    "Falta departamento_id o municipio_id (catálogo C807)": "Missing department_id or municipality_id (C807 catalog)",
    "Guía no encontrada": "Waybill not found",
    "Guía no encontrada en envios_c807": "Waybill not found in C807 shipments",
    "No hay guías pendientes de impresión": "No waybills pending printing",
    "Lista de guías vacía": "Empty waybills list",
    "Lista de pedidos vacía": "Empty orders list",
    "Genera la etiqueta primero": "Generate the label first",
    "Configura ShipStation en Admin → Ajustes primero": "Configure ShipStation in Admin → Settings first",
    "Configura ShipStation primero (api_key, api_secret y dirección de origen).": "Configure ShipStation first (api_key, api_secret and origin address).",
    "Configura api_key y api_secret antes de probar.": "Configure api_key and api_secret before testing.",
    "ShipStation no está configurado completamente. Revisa Admin → Ajustes → ShipStation.": "ShipStation is not fully configured. Check Admin → Settings → ShipStation.",
    "Falta la Production API Key de ShipStation V2.": "Missing ShipStation V2 Production API Key.",
    "Ingresa la Production API Key V2 para probar.": "Enter the Production API Key V2 to test.",
    "API Key V2 inválida o sin permisos.": "Invalid API Key V2 or missing permissions.",
    "El usuario API no puede estar vacío": "The API user cannot be empty",
    "Para modo real necesitas usuario y contraseña configurados": "Real mode requires configured username and password",
    "Falta el ZIP de origen en la configuración de ShipStation.": "Missing origin ZIP in the ShipStation configuration.",
    "Falta el destinatario.": "Recipient is missing.",
    "Límite de tasa de ShipStation V2 excedido. Reintenta en unos segundos.": "ShipStation V2 rate limit exceeded. Retry in a few seconds.",
    "Límite de tasa excedido. Reintenta en {reset}s.": "Rate limit exceeded. Retry shortly.",
    "Para usar HAZMAT (etiqueta con ": "To use HAZMAT (label with ",
    "tipo_servicio inválido (SER | CCE)": "invalid service_type (SER | CCE)",

    // Storage / SMTP / infra
    "Storage not configured": "Storage not configured",
    "Storage not configured. Set EMERGENT_LLM_KEY.": "Storage not configured. Set EMERGENT_LLM_KEY.",
    "SMTP no está configurado o el servidor rechazó el mensaje. Revisa Ajustes → SMTP.": "SMTP is not configured or the server rejected the message. Check Settings → SMTP.",
    "Configura SMTP en Admin → Ajustes para enviar emails": "Configure SMTP in Admin → Settings to send emails",
    "No hay destinatarios configurados. Guarda al menos un correo primero.": "No recipients configured. Save at least one email first.",
    "La traducción automática falló. Verifica EMERGENT_LLM_KEY.": "Automatic translation failed. Check EMERGENT_LLM_KEY.",
    "Fecha inválida (usa formato YYYY-MM-DD).": "Invalid date (use YYYY-MM-DD format).",
    "La fecha debe ser al menos 2 meses (60 días) en el futuro.": "The date must be at least 2 months (60 days) in the future.",
    "Formato inválido, usa YYYY-MM": "Invalid format, use YYYY-MM",
    "No encontrado": "Not found",

    // Generic fallback
    "Algo salió mal. Inténtalo de nuevo.": "Something went wrong. Please try again.",
};

// Dynamic patterns (regex → English replacer).
// Applied when a message contains variable content that won't match the static map.
export const DYNAMIC_ERROR_PATTERNS = [
    { re: /^Producto (.+?) no encontrado$/, en: (m) => `Product ${m[1]} not found` },
    { re: /^Producto (.+?) no está disponible para envíos a Estados Unidos$/, en: (m) => `Product ${m[1]} is not available for shipments to the United States` },
    { re: /^Stock insuficiente para (.+?) \(disponibles: (\d+)\)$/, en: (m) => `Insufficient stock for ${m[1]} (available: ${m[2]})` },
    { re: /^Solo quedan (\d+) unidad\(es\) disponible\(s\) para apartar\.$/, en: (m) => `Only ${m[1]} unit(s) available to hold.` },
    { re: /^Compra mínima de \$([0-9.]+) requerida$/, en: (m) => `Minimum purchase of $${m[1]} required` },
    { re: /^Compra mínima de \$([0-9.]+) requerida para este código$/, en: (m) => `Minimum purchase of $${m[1]} required for this code` },
    { re: /^Este código solo aplica para (.+)$/, en: (m) => `This code only applies to ${m[1]}` },
    { re: /^Ya existe un código '(.+?)'$/, en: (m) => `A code '${m[1]}' already exists` },
    { re: /^El SKU (.+?) ya existe en el catálogo\. .+$/, en: (m) => `SKU ${m[1]} already exists in the catalog. Assign a different one to the incoming product before releasing.` },
    { re: /^Pedido sin dirección USA completa\. Faltan: (.+)$/, en: (m) => `Order with incomplete USA address. Missing: ${m[1]}` },
    { re: /^Faltan campos: (.+)$/, en: (m) => `Missing fields: ${m[1]}` },
    { re: /^Estado inválido para método (.+)$/, en: (m) => `Invalid status for method ${m[1]}` },
    { re: /^Estado inválido\. Permitidos: (.+)$/, en: (m) => `Invalid status. Allowed: ${m[1]}` },
    { re: /^C807 error: (.+)$/, en: (m) => `C807 error: ${m[1]}` },
    { re: /^C807 rechazó la guía: (.+)$/, en: (m) => `C807 rejected the waybill: ${m[1]}` },
    { re: /^C807 rechazó la guía de prueba: (.+)$/, en: (m) => `C807 rejected the test waybill: ${m[1]}` },
    { re: /^C807 get_pdf falló: (.+)$/, en: (m) => `C807 get_pdf failed: ${m[1]}` },
    { re: /^No se pudo generar la guía: (.+)$/, en: (m) => `Could not generate waybill: ${m[1]}` },
    { re: /^No se pudo contactar ShipStation V2: (.+)$/, en: (m) => `Could not reach ShipStation V2: ${m[1]}` },
    { re: /^No se pudo contactar ShipStation: (.+)$/, en: (m) => `Could not reach ShipStation: ${m[1]}` },
    { re: /^ShipStation error: (.+)$/, en: (m) => `ShipStation error: ${m[1]}` },
    { re: /^Error autenticando con PayPal: (.+)$/, en: (m) => `PayPal authentication error: ${m[1]}` },
    { re: /^Error capturando pago: (.+)$/, en: (m) => `Error capturing payment: ${m[1]}` },
    { re: /^Error creando sesión Stripe: (.+)$/, en: (m) => `Error creating Stripe session: ${m[1]}` },
    { re: /^Error enviando correo de prueba: (.+)$/, en: (m) => `Error sending test email: ${m[1]}` },
    { re: /^Error enviando email: (.+)$/, en: (m) => `Error sending email: ${m[1]}` },
    { re: /^PayPal error: (.+)$/, en: (m) => `PayPal error: ${m[1]}` },
    { re: /^Sesión inválida: (.+)$/, en: () => `Invalid session` },
    { re: /^Archivo JSON inválido: (.+)$/, en: (m) => `Invalid JSON file: ${m[1]}` },
    { re: /^Configura la dirección de origen en Ajustes → ShipStation\. Faltan: (.+)$/, en: (m) => `Configure the origin address in Settings → ShipStation. Missing: ${m[1]}` },
    { re: /^Te faltan (.+?) para envío gratis\.$/, en: (m) => `${m[1]} away from free shipping.` },
];

/**
 * Translate a Spanish backend error to English if the user is on the English site.
 * @param {string} msg — the raw Spanish message from the backend
 * @param {boolean} isEnglish — whether to translate to English
 * @returns translated message (or original if no translation available)
 */
export function translateErrorMessage(msg, isEnglish) {
    if (!msg || !isEnglish) return msg;
    if (typeof msg !== "string") return msg;
    // Exact match first
    if (ERROR_MESSAGE_MAP[msg]) return ERROR_MESSAGE_MAP[msg];
    // Pattern matching
    for (const { re, en } of DYNAMIC_ERROR_PATTERNS) {
        const m = msg.match(re);
        if (m) return en(m);
    }
    // Fallback: return the original Spanish message
    return msg;
}
